import { DoubleBooking } from 'components/business/checkout/DoubleBookingWarning';
import { StoreType } from '@tock/shared/utils/connect';
import { core } from '@tock/coretypes';
import { createSelector } from 'reselect';
import { getDoubleBookingsHash } from './patronSelectors';
import { getFeatures } from './featuresSelector';
import { isOrderVariety } from '@tock/shared/utils/offerings/varietyQueries';
import { isVailReservation } from 'utils/VailDoubleBooking';

export const purchaseStateSelector = (state: StoreType) => state.purchase;

export const calculatedPriceSelector = (state: StoreType) => state.purchase.calculatedPrice;

export const getCurrentBookingDoubleBookings = createSelector(
  getDoubleBookingsHash,
  calculatedPriceSelector,
  (
    doubleBookingsHash: {
      [dateTime: string]: core.PastPurchase[];
    },
    calculatedPrice?: core.PurchaseOrder
  ) => {
    if (!calculatedPrice?.dateTime) {
      return [];
    }

    return doubleBookingsHash[calculatedPrice?.dateTime] ?? [];
  }
);

export const getDoubleBookingStatus = createSelector(
  (state: StoreType) => state.app.config?.business,
  (state: StoreType) => state.lock.currentLock,
  (state: StoreType) => state.app.patron,
  getFeatures,
  getCurrentBookingDoubleBookings,
  (
    currentBusiness,
    currentLock,
    patron,
    { CONSUMER_VAIL_RESTRICT_B2B_RESERVATIONS },
    doubleBookings: core.PastPurchase[]
  ): DoubleBooking => {
    if (!currentBusiness || !currentLock || doubleBookings.length === 0) {
      return {
        type: 'DOUBLE_BOOKING_NONE',
        restrictBooking: false,
      };
    }

    const isConciergeBooking = patron?.email !== doubleBookings[0].dinerPatron[0].email;
    const isVailRestrictB2BEnabled = !!CONSUMER_VAIL_RESTRICT_B2B_RESERVATIONS;
    const isCurrentLockNotToGo = !isOrderVariety(currentLock.ticketTypeVariety);
    const isVailBooking = doubleBookings.some((booking) => isVailReservation(booking));
    const isBookingMultiple =
      doubleBookings.length > 1 &&
      doubleBookings.some((booking) => booking.business?.id === currentBusiness.id);
    if (doubleBookings.length === 1) {
      const otherBooking = doubleBookings[0];
      if (currentBusiness.id === otherBooking.business?.id) {
        if (isConciergeBooking) {
          return {
            type: 'DOUBLE_BOOKING_SAME_RESTAURANT_CONCIERGE',
            restaurantName: undefined,
            restrictBooking:
              isVailRestrictB2BEnabled &&
              isCurrentLockNotToGo &&
              !isOrderVariety(otherBooking.ticketType?.variety),
          };
        }
        return {
          type: 'DOUBLE_BOOKING_SAME_RESTAURANT',
          restaurantName: currentBusiness.name || undefined,
          restrictBooking:
            isVailRestrictB2BEnabled &&
            isCurrentLockNotToGo &&
            !isOrderVariety(otherBooking.ticketType?.variety),
        };
      } else {
        return {
          type: 'DOUBLE_BOOKING_OTHER_RESTAURANT',
          restrictBooking:
            isVailRestrictB2BEnabled && isCurrentLockNotToGo && isVailReservation(otherBooking),
        };
      }
    } else {
      return {
        type: 'DOUBLE_BOOKING_MULTIPLE',
        restrictBooking:
          isVailRestrictB2BEnabled && isCurrentLockNotToGo && (isBookingMultiple || isVailBooking),
      };
    }
  }
);

/**
 * Returns true if the total cost of a transferred booking exceeds the amount paid by the original
 * booking owner. This can happen if taxes or large party deposits change between booking + transfer.
 * @see [TK-10078](https://tock.atlassian.net/browse/TK-10078)
 *
 * @param originalCentsPaid - The total amount paid by the original reservation owner
 * @param purchaseOrder     - Purchase order for the transfer
 */
export const getHasTransferredPriceChanged = createSelector(
  (state: StoreType) => state.purchase.calculatedPrice,
  (state: StoreType) => state.purchase.preTransferNetAmountCents,
  (purchaseOrder, originalCentsPaid = 0) => {
    if (!purchaseOrder || !purchaseOrder.transferPurchaseToken) {
      return false;
    }

    const { discount, totalPrice } = purchaseOrder;
    const transferDiscountCents =
      discount.find((d) => d.description === 'Transfer Discount')?.amountCents ?? 0;

    return totalPrice + transferDiscountCents !== originalCentsPaid;
  }
);
